/**
 * TC dashboard v2 — Operations tab model (TC-DASHBOARD-V2-DESIGN §4.1). The returned shape is the N11 contract for
 * GET /api/dashboard/operations; today it is assembled from existing endpoints.
 * Each source is independent: a failing one fills `errors[card]` and leaves the other cards intact.
 */

import ApiService from '../api.service';
import { financeReportsService } from '../accounting/financeReports.service';
import { unwrapData } from '../../utils/apiConvert';
import { can } from '../../utils/roleUtils';
import { delta } from '../../utils/dashboard/metrics';
import { toIso } from '../../utils/dashboard/period';

const n = (v) => Number(v) || 0;
const body = (res) => res?.data?.data ?? {};
const failText = (err) => (err?.response?.status === 403 ? 'No access' : 'Could not load');
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const daysBetween = (fromIso, toIsoDate) => {
  const [y1, m1, d1] = fromIso.split('-').map(Number);
  const [y2, m2, d2] = toIsoDate.split('-').map(Number);
  return Math.round((new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1)) / 86400000);
};

const NO_ACCESS = { response: { status: 403 } };
// Only ask for what the user may see: cash today needs finance read, the pipeline work order read (deny by default)
const allowed = (user, feature) => can(user, feature, 'read');

// mock: override the demo values (tests); today: the calendar day the counts are for; user: the signed-in user
export const loadOperations = async (today = new Date(), mock = {}, user = null) => {
  const t = toIso(today);
  const in7 = toIso(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7));
  const in30 = toIso(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30));

  const [todayRes, opsRes, woTodayRes, tasksRes, approvalsRes, soonRes, weekRes, companySoonRes, companyWeekRes] = await Promise.allSettled([
    allowed(user, 'finance') ? financeReportsService.getDailyCashRange({ from: t, to: t }) : Promise.reject(NO_ACCESS),
    allowed(user, 'work_order') ? ApiService.getOperationsSummary() : Promise.reject(NO_ACCESS),
    allowed(user, 'work_order') ? ApiService.get('/api/dashboard/work-orders/today') : Promise.reject(NO_ACCESS),
    ApiService.get('/api/tasks/my-counts'),
    allowed(user, 'finance') ? ApiService.get('/api/dashboard/approvals') : Promise.reject(NO_ACCESS),
    ApiService.get('/api/document-expiry', { params: { page: 0, size: 4, fromDate: t, toDate: in30 } }),
    ApiService.get('/api/document-expiry', { params: { page: 0, size: 1, fromDate: t, toDate: in7 } }),
    ApiService.get('/api/document-expiry', { params: { kind: 'COMPANY', page: 0, size: 3, fromDate: t, toDate: in30 } }),
    ApiService.get('/api/document-expiry', { params: { kind: 'COMPANY', page: 0, size: 1, fromDate: t, toDate: in7 } }),
  ]);
  const errors = {};

  let cashToday = { in: 0, out: 0, net: 0 };
  if (todayRes.status === 'fulfilled') {
    const row = (todayRes.value || [])[0] || {};
    cashToday = { in: n(row.cashIn), out: n(row.cashOut), net: n(row.net) };
  } else errors.cashToday = failText(todayRes.reason);

  let pipeline = { pending: 0, inProgress: 0, completed: 0, invoiced: 0, pendingReview: 0 };
  if (opsRes.status === 'fulfilled') {
    const o = unwrapData(opsRes.value) || {};
    pipeline = { pending: n(o.pending), inProgress: n(o.inProgress), completed: n(o.completed), invoiced: n(o.invoiced), pendingReview: n(o.pendingReview) };
  } else { errors.pipeline = failText(opsRes.reason); errors.inProgress = errors.pipeline; }

  let tasks = { pending: 0, inProgress: 0, doneToday: 0 };
  if (tasksRes.status === 'fulfilled') {
    const k = unwrapData(tasksRes.value) || {};
    tasks = { pending: n(k.pending), inProgress: n(k.inProgress), doneToday: n(k.doneToday) };
  } else { errors.tasks = failText(tasksRes.reason); }

  let dueToday = { count: 0, overdue: 0 };
  let completedToday = { count: 0, delta: null };
  if (woTodayRes.status === 'fulfilled') {
    const w = unwrapData(woTodayRes.value) || {};
    dueToday = { count: n(w.dueToday), overdue: n(w.overdue), series: (w.dueSeries || []).map(n) };
    completedToday = { count: n(w.completedToday), delta: delta(w.completedToday, w.completedYesterday), series: (w.completedSeries || []).map(n) };
  } else { errors.dueToday = failText(woTodayRes.reason); errors.completedToday = errors.dueToday; }

  let expiring = { total: 0, items: [] };
  if (soonRes.status === 'fulfilled') {
    const page = body(soonRes.value);
    const items = (page.content || []).map((r) => ({
      id: r.documentId,
      person: [r.firstName, r.lastName].filter(Boolean).join(' ') || 'Employee',
      document: r.documentType?.name || r.documentType || 'Document',
      client: r.clientName || '',
      daysLeft: r.expiryDate ? daysBetween(t, String(r.expiryDate).slice(0, 10)) : null,
    })).sort((a, b) => (a.daysLeft ?? Infinity) - (b.daysLeft ?? Infinity));
    expiring = { total: n(page.totalElements ?? page.total_elements), items };
  } else errors.expiring = failText(soonRes.reason);
  const expiringWeek = weekRes.status === 'fulfilled' ? n(body(weekRes.value).totalElements) : 0;

  // Company documents (trade licence, establishment card, ...) are listed apart from the employee ones.
  let companyExpiring = { total: 0, items: [] };
  if (companySoonRes.status === 'fulfilled') {
    const page = body(companySoonRes.value);
    companyExpiring = {
      total: n(page.totalElements),
      items: (page.content || []).map((r) => ({
        id: r.document_group_id,
        client: r.client_name || 'Client',
        document: r.document_type_label || r.document_type || 'Document',
        daysLeft: r.expiry_date ? daysBetween(t, String(r.expiry_date).slice(0, 10)) : null,
      })),
    };
  }
  const companyWeek = companyWeekRes.status === 'fulfilled' ? n(body(companyWeekRes.value).totalElements) : 0;

  let a = { topups: 0, expenses: 0, drawings: 0, disbursementReviews: 0 };
  if (approvalsRes.status === 'fulfilled') {
    const k = unwrapData(approvalsRes.value) || {};
    a = { topups: n(k.topups), expenses: n(k.expenses), drawings: n(k.drawings), disbursementReviews: n(k.disbursementReviews) };
  } else if (approvalsRes.reason?.response?.status !== 403) errors.approvals = failText(approvalsRes.reason);
  const approvalsTotal = a.topups + a.expenses + a.drawings + a.disbursementReviews;
  const attention = [
    dueToday.overdue > 0 && {
      key: 'overdue', tone: 'red',
      title: `${plural(dueToday.overdue, 'work order is', 'work orders are')} overdue`,
      action: { label: 'Open', to: '/work-orders' },
    },
    expiringWeek > 0 && {
      key: 'expiring', tone: 'amber',
      title: `${plural(expiringWeek, 'employee document expires', 'employee documents expire')} within 7 days`,
      action: { label: 'Notify clients', to: '/document-expiry' },
    },
    companyWeek > 0 && {
      key: 'companyExpiring', tone: 'amber',
      title: `${plural(companyWeek, 'company document expires', 'company documents expire')} within 7 days`,
      action: { label: 'Open', to: '/document-expiry?kind=COMPANY' },
    },
    approvalsTotal > 0 && {
      key: 'approvals', tone: 'blue',
      title: `${plural(approvalsTotal, 'item', 'items')} waiting for your approval`,
      detail: [
        a.topups && plural(a.topups, 'wallet top-up', 'wallet top-ups'),
        a.expenses && plural(a.expenses, 'expense', 'expenses'),
        a.drawings && plural(a.drawings, 'owner drawing', 'owner drawings'),
        a.disbursementReviews && plural(a.disbursementReviews, 'government fee receipt', 'government fee receipts'),
      ].filter(Boolean).join(' · '),
      action: { label: 'Review', to: a.topups ? '/wallets?tab=topups' : a.expenses ? '/accounting/expenses' : a.drawings ? '/accounting/drawings' : '/accounting/disbursement-reviews' },
    },
    pipeline.pendingReview > 0 && {
      key: 'review', tone: 'teal',
      title: `${plural(pipeline.pendingReview, 'portal request', 'portal requests')} to review`,
      action: { label: 'Review', to: '/work-orders?status=PENDING_REVIEW' },
    },
  ].filter(Boolean);

  return {
    dueToday,
    inProgress: { count: pipeline.inProgress, delta: null },
    completedToday,
    cashToday,
    attention,
    tasks,
    pipeline,
    expiring,
    companyExpiring,
    errors,
  };
};
